import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../api/useHttpAdapter";
import { AuthState, AuthCredentials, SignupResponse, AuthUser } from "../../../types/auth-types";

export const loginUser = createAsyncThunk<AuthUser, AuthCredentials, { rejectValue: string }>(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/identity/login", { email, password });
      const user = response?.data?.user;
      if (!user) return rejectWithValue("No user data returned");
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred during login");
    }
  }
);

export const signupUser = createAsyncThunk<SignupResponse, AuthCredentials, { rejectValue: string }>(
  "auth/signup",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      await axiosInstance.post("/identity/signup", { email, password });
      return { user: null, session: null, needsEmailConfirmation: true };
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred during signup");
    }
  }
);

const initialState: AuthState = {
  currentUser: null,
  error: null,
  currentUserLoading: false,
  createCurrentUserLoading: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentUser: (state, action: PayloadAction<AuthUser | null>) => {
      state.currentUser = action.payload;
    },
    logout: (state) => {
      state.currentUser = null;
      state.error = null;
      localStorage.removeItem("account_details");
      sessionStorage.removeItem("account_details");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.currentUserLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.currentUserLoading = false;
        state.currentUser = action.payload;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.currentUserLoading = false;
        state.error = action.payload || "Login failed";
      })
      .addCase(signupUser.pending, (state) => {
        state.createCurrentUserLoading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state) => {
        state.createCurrentUserLoading = false;
        state.error = null;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.createCurrentUserLoading = false;
        state.error = action.payload || "Signup failed";
      });
  },
});

export const { clearError, setCurrentUser, logout } = authSlice.actions;
export default authSlice.reducer;
